"use client";

import CountUp from "@/components/CountUp/CountUp";
import DotsPattern from "@/components/CSSPatterns/DotsPattern/DotsPattern";
import { FunctionComponent } from "react";
import { FormattedMessage } from "react-intl";

type StatItem = {
  value: string;
  label: React.ReactNode; // FormattedMessage ok
};

type StatsGlanceProps = {
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  stats: StatItem[];
  footnote?: React.ReactNode;
  className?: string;
};

function parseAnimatedValue(raw: string): null | {
  prefix: string;
  to: number;
  suffix: string;
} {
  const v = raw.trim();

  // We keep "24/7" static (or anything with slash)
  if (v.includes("/")) return null;

  // Match: optional +/-, number (int or decimal), optional suffix (%, ×, etc.)
  // Examples: "+15%", "2×", "100%", "-3.5%"
  const m = v.match(/^([+-]?)\s*(\d+(?:[.,]\d+)?)\s*([^0-9\s].*)?$/);
  if (!m) return null;

  const prefix = m[1] ?? "";
  const numStr = (m[2] ?? "").replace(",", ".");
  const suffix = (m[3] ?? "").trim();

  const to = Number(numStr);
  if (!Number.isFinite(to)) return null;

  return { prefix, to, suffix };
}

function decimalsFrom(rawNumber: number) {
  const s = String(rawNumber);
  const dot = s.indexOf(".");
  return dot >= 0 ? Math.min(2, s.length - dot - 1) : 0;
}

const StatsGlance: FunctionComponent<StatsGlanceProps> = ({
  eyebrow,
  title,
  subtitle,
  stats,
  footnote,
  className = "",
}) => {
  return (
    <section
      className={[
        "relative overflow-hidden py-16 md:py-24",
        // soft, premium background that stays in your blue/white identity
        "bg-gradient-to-b from-white via-blue-50/40 to-white",
        className,
      ].join(" ")}
    >
      {/* Ambient glows (blue + primary violet) */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/2 top-[-140px] h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-blue-500/10 blur-3xl" />
        <div className="absolute right-[-120px] top-10 h-[420px] w-[420px] rounded-full bg-violet-500/12 blur-3xl" />
        <div className="absolute left-[-140px] top-40 h-[420px] w-[420px] rounded-full bg-blue-400/10 blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(59,130,246,0.10),transparent_55%)]" />
      </div>

      {/* Dots (subtle) */}
      <div className="absolute inset-0 pointer-events-none opacity-60">
        {/* centre/droite, comme sur votre hero */}
        <div className="md:block absolute right-[10%] top-16">
          <DotsPattern right={150} />
        </div>
        {/* gauche bas pour équilibrer */}
        <div className="md:block absolute left-[8%] bottom-16 opacity-70">
          <DotsPattern left={150} />
        </div>
      </div>

      <div className="relative mx-auto w-full max-w-6xl px-6 md:px-10">
        {/* Header */}
        <div className="text-center">
          {eyebrow ? (
            <div className="inline-flex items-center gap-2 rounded-full bg-white/70 px-4 py-1.5 text-[11px] md:text-xs font-semibold tracking-[0.22em] uppercase text-blue-700 ring-1 ring-blue-100 shadow-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />
              {eyebrow}
            </div>
          ) : null}

          <h2 className="mt-5 text-4xl md:text-6xl font-semibold tracking-tight text-zinc-900">
            {title}
          </h2>

          {subtitle ? (
            <div className="mx-auto mt-4 max-w-2xl text-base md:text-lg text-zinc-600">
              {subtitle}
            </div>
          ) : null}
        </div>

        {/* Premium container */}
        <div className="mt-12 md:mt-16">
          <div
            className={[
              "relative rounded-3xl p-[1px]",
              // subtle gradient border using blue + your primary violet
              "bg-gradient-to-br from-blue-200/70 via-white to-violet-200/70",
              "shadow-[0_30px_90px_-55px_rgba(59,130,246,0.55)]",
            ].join(" ")}
          >
            <div className="rounded-3xl bg-white/80 backdrop-blur-xl ring-1 ring-zinc-200/70">
              {/* Optional top bar (very subtle) */}
              <div className="flex items-center justify-between gap-3 border-b border-zinc-200/60 bg-white/60 px-5 md:px-7 py-4">
                <div className="flex items-center gap-2 text-xs text-zinc-500">
                  <span className="h-2 w-2 rounded-full bg-blue-600/80" />
                  <span className="hidden sm:inline">
                    <FormattedMessage
                      id="home.stats.performance.title"
                      defaultMessage="Performance Overview"
                    />
                  </span>
                  <span className="sm:hidden">
                    <FormattedMessage
                      id="home.stats.performance.title.md"
                      defaultMessage="Performance"
                    />
                  </span>
                </div>
                <span className="rounded-full bg-white px-3 py-1 text-xs text-zinc-500 ring-1 ring-zinc-200/70">
                  <FormattedMessage
                    id="home.stats.keyNumbers.title.md"
                    defaultMessage="KPIs"
                  />
                </span>
              </div>

              {/* Stats cards */}
              <div className="px-5 md:px-7 py-8 md:py-10">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                  {stats.map((s, idx) => {
                    const parsed = parseAnimatedValue(s.value);

                    return (
                      <div
                        key={idx}
                        className={[
                          "group rounded-2xl p-4 md:p-5",
                          "bg-white ring-1 ring-zinc-200/70",
                          "shadow-[0_18px_50px_-35px_rgba(0,0,0,0.25)]",
                          "transition-all duration-300",
                          "hover:-translate-y-0.5 hover:shadow-[0_24px_70px_-40px_rgba(59,130,246,0.35)]",
                        ].join(" ")}
                      >
                        {/* Value */}
                        <div className="text-4xl md:text-5xl font-semibold tracking-tight text-blue-700 text-center">
                          {parsed ? (
                            <CountUp
                              from={Math.max(0, Math.floor(parsed.to * 0.1))} // starts low (ex: 1 for 15)
                              to={parsed.to}
                              prefix={parsed.prefix}
                              suffix={parsed.suffix}
                              decimals={decimalsFrom(parsed.to)}
                              durationMs={1100}
                              delayMs={idx * 120} // small stagger
                              once
                            />
                          ) : (
                            s.value
                          )}
                        </div>

                        {/* Label */}
                        <div className="mt-2 text-sm md:text-base text-zinc-600 text-center">
                          {s.label}
                        </div>

                        {/* Tiny accent line on hover */}
                        <div
                          aria-hidden="true"
                          className="mt-4 h-px w-full bg-gradient-to-r from-transparent via-blue-300/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"
                        />
                      </div>
                    );
                  })}
                </div>

                {footnote ? (
                  <div className="mt-8 text-center text-xs md:text-sm text-zinc-500">
                    {footnote}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StatsGlance;
